import { useState, useMemo, useContext, useEffect } from 'react';
import merge from 'lodash/merge';
import get from 'lodash/get';
import {
  and,
  Categorization,
  categorizationHasCategory,
  deriveLabelForUISchemaElement,
  isVisible,
  optionIs,
  RankedTester,
  rankWith,
  StatePropsOfLayout,
  uiTypeIs,
} from '@jsonforms/core';
import { TranslateProps, withJsonFormsLayoutProps, withTranslateProps } from '@jsonforms/react';
import { AjvProps, MaterialLayoutRenderer, MaterialLayoutRendererProps, withAjvProps } from './layout';
import CustomComponent from './component';
import { EditorContext } from '../../context';
import { checkNextStep } from './utils';

export const materialCategorizationStepperTester: RankedTester = rankWith(
  1000,
  and(uiTypeIs('Categorization'), categorizationHasCategory, optionIs('variant', 'stepper'))
);

export interface CategorizationStepperState {
  activeCategory: number;
}

export interface MaterialCategorizationStepperLayoutRendererProps extends StatePropsOfLayout, AjvProps, TranslateProps {
  data: any;
}

export const MaterialCategorizationStepperLayoutRenderer = (
  props: MaterialCategorizationStepperLayoutRendererProps
) => {
  const [activeCategory, setActiveCategory] = useState<number>(0);

  const [isScanningTags, setIsScanningTags] = useState(false);
  const { assetCompsData, scanTags, scanningTags, setScanningTags, stopScan, scanFailed, disableScanStop } =
    useContext(EditorContext);

  const { data, path, renderers, schema, uischema, visible, cells, config, ajv, t, enabled } = props;
  const categorization = uischema as Categorization;
  const appliedUiSchemaOptions = merge({}, config, uischema.options);

  useEffect(() => {
    const currentCategory = categorization.elements
      .map((category: any, idx: number) => {
        const checkStep = checkNextStep(category, data, undefined, ajv);
        return {
          stepNumber: idx,
          stepStatus: checkStep,
        };
      })
      .find((cat: any) => !cat.stepStatus);
    // setActiveCategory(categories[currentCategory?.stepNumber ?? 0] ? currentCategory?.stepNumber ?? 0 : 0);
    setActiveCategory(currentCategory?.stepNumber ?? 0);
  }, []);

  useEffect(() => {
    if (isScanningTags && !scanningTags.includes(assetCompsData?.activeNode?.id) && !scanFailed) {
      setActiveCategory(activeCategory + 1);
    }
  }, [scanningTags, isScanningTags, scanFailed]);

  const categories = useMemo(
    () => categorization.elements.filter((category: any) => isVisible(category, data, undefined as any, ajv, undefined)),
    [categorization, data, ajv]
  );

  // const filterCat = useMemo(
  //   () =>
  //     categorization.elements.map((category: any) => {
  //       return isCustomVisible(category, data, undefined, ajv);
  //     }),
  //   [categorization, data, ajv]
  // );

  const childProps: MaterialLayoutRendererProps = {
    elements: categories[activeCategory].elements,
    schema,
    path,
    direction: 'column',
    visible,
    renderers,
    cells,
  };

  const tabLabels = useMemo(() => {
    return categories.map((e: any) => deriveLabelForUISchemaElement(e, t));
  }, [categories, t]);

  if (!visible) {
    return null;
  }

  // console.log(childProps, uischema, tabLabels, 'childProps');

  const handleStep = (cat: number, action: string) => {
    if (action === 'scan') {
      const { gatewayId, component, activeNode } = assetCompsData;
      if (!scanningTags.includes(activeNode?.id)) {
        setIsScanningTags(true);
        scanTags(gatewayId ?? '', component);
      }
    } else {
      setActiveCategory(cat);
    }
  };

  const onStopScanning = () => {
    const { gatewayId, component } = assetCompsData;

    setIsScanningTags(false);
    // setScanningTags(scanningTags.filter((tag: string) => tag !== assetCompsData?.activeNode?.id));
    stopScan(gatewayId ?? '', component);
  };

  return (
    <>
      {appliedUiSchemaOptions.showNavButtons && (
        <CustomComponent
          activeCategory={activeCategory}
          handleStep={handleStep}
          categories={categories}
          info={get(uischema, `elements.${activeCategory}.info`, '')}
          // isScannable={schema?.options?.scan_button}
          enabled={enabled}
          uischema={uischema}
          schema={schema}
          assetCompsData={assetCompsData}
          isScanningTags={
            // get(categories, `${activeCategory}.category`) === 'scan_result' &&
            // isScanningTags &&
            scanningTags.includes(assetCompsData?.activeNode?.id)
          }
          onStopScanning={onStopScanning}
          disableScanStop={disableScanStop}
        />
      )}
      <div>
        <MaterialLayoutRenderer {...childProps} />
      </div>
    </>
  );
};

export default withAjvProps(withTranslateProps(withJsonFormsLayoutProps(MaterialCategorizationStepperLayoutRenderer)));
